import * as PropertyTypes from 'customer-data-objects/property/PropertyTypes';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { isValidCondition } from '../../filterQueryFormat/logic/LogicGroup';
import { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { usePropertyConditionChange } from '../../hooks/usePropertyConditionChange';
import { useStrategy } from '../../hooks/useStrategy';
import { useValidateCondition } from '../../hooks/useValidateCondition';
import EditorConditionValueInput from '../editor/EditorConditionValueInput';
import EditorIncludeObjectsWithNoValueSetInput from '../editor/EditorIncludeObjectsWithNoValueSetInput';
import FormattedMessage from 'I18n/components/FormattedMessage';
import OperatorSelect from './OperatorSelect';
import PropertySelect from './PropertySelect';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import styled from 'foundations-theming/utils';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ValueInput = styled.div.withConfig({
  displayName: "PropertyFilterUI__ValueInput",
  componentId: "d5xr1m-0"
})(({
  propertyType,
  use
}) => use === 'horizontal' ? {
  // EditorConditionValueInput_FixedStyleInput
  '> *': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    // InputComponentWrapper
    '> *': {
      margin: '0 !important',
      minWidth: propertyType !== PropertyTypes.BOOLEAN ? '300px' : undefined
    }
  }
} : {
  width: '100%'
});
const SelectInput = styled.div.withConfig({
  displayName: "PropertyFilterUI__SelectInput",
  componentId: "d5xr1m-1"
})(({
  use
}) => use === 'horizontal' ? {
  minWidth: '300px'
} : {
  width: '100%'
});
const PropertyFilterUI = ({
  additionalComponents,
  disabled,
  limitOperators,
  hidePropertySelect,
  inputUse,
  multiCollapseLimit,
  onChange,
  precedingInputComponents = [],
  showExtraInputs,
  readOnly,
  requiredPropertyName,
  use = 'horizontal'
}) => {
  const {
    dispatch,
    internalDraftValue,
    isLoadingViewerData,
    props: {
      baseFilterFamily
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    condition,
    conditionPath,
    property,
    onConditionChange,
    onPropertyChange
  } = usePropertyConditionChange(onChange);
  const {
    isIncludeObjectsWithNoValueSetCheckboxEnabled
  } = useStrategy();
  const {
    validateLogicGroup
  } = useValidateCondition();
  const [hasPropertiesLoaded, setHasPropertiesLoaded] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  useEffect(() => {
    if (internalDraftValue && !isLoadingViewerData && !isValidated) {
      dispatch({
        type: 'internalDraftValue',
        payload: validateLogicGroup(internalDraftValue)
      });
      setIsValidated(true);
    }
  }, [isLoadingViewerData, internalDraftValue, validateLogicGroup, dispatch, isValidated]);
  const prevRequiredPropertyName = useRef(requiredPropertyName);
  useEffect(() => {
    if (prevRequiredPropertyName.current !== requiredPropertyName) {
      const propertyName = property ? property.name : undefined;
      if (condition && requiredPropertyName && requiredPropertyName !== propertyName) {
        onConditionChange();
      }
      prevRequiredPropertyName.current = requiredPropertyName;
    }
  }, [condition, onConditionChange, property, requiredPropertyName]);
  const prevDisabled = useRef(disabled);
  const isConditionValid = !!condition && isValidCondition(condition);
  useEffect(() => {
    if (prevDisabled.current && !disabled && !isConditionValid) {
      onPropertyChange();
    }
    if (prevDisabled.current !== disabled) {
      prevDisabled.current = disabled;
    }
  }, [disabled, isConditionValid, onPropertyChange]);
  const precedingInputs = useMemo(() => {
    return precedingInputComponents.map((precedingInputComponent, index) => /*#__PURE__*/_jsx(SelectInput, {
      use: use,
      children: precedingInputComponent
    }, `precedingInputComponent-${index}`));
  }, [precedingInputComponents, use]);
  const shouldShowLoadingSpinner = !internalDraftValue || !isValidated || isLoadingViewerData;
  const shouldShowOnlyPropertySelectWhenDisabled = disabled && !isConditionValid;
  const isReadOnly = readOnly && isConditionValid;
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: use === 'horizontal' ? 'row' : 'column',
    gap: "200",
    flexWrap: "wrap",
    children: [precedingInputs, shouldShowLoadingSpinner ? /*#__PURE__*/_jsx(LoadingSpinner, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.propertyFilter.loading"
      }),
      margin: "0",
      paddingBlock: "100",
      paddingInline: "200",
      showLabel: true,
      size: "sm"
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(SelectInput, {
        use: use,
        children: /*#__PURE__*/_jsx(PropertySelect, {
          disabled: disabled,
          filterFamily: baseFilterFamily,
          inputUse: inputUse,
          property: shouldShowOnlyPropertySelectWhenDisabled ? undefined : property,
          onChange: onPropertyChange,
          onPropertiesLoaded: () => setHasPropertiesLoaded(true),
          readOnly: isReadOnly,
          requiredPropertyName: requiredPropertyName,
          hidePropertySelect: hidePropertySelect
        })
      }), !shouldShowOnlyPropertySelectWhenDisabled && hasPropertiesLoaded && /*#__PURE__*/_jsxs(_Fragment, {
        children: [property && /*#__PURE__*/_jsx(SelectInput, {
          use: use,
          children: /*#__PURE__*/_jsx(OperatorSelect, {
            disabled: disabled,
            limitOperators: limitOperators,
            filterFamily: baseFilterFamily,
            inputUse: inputUse,
            condition: condition,
            conditionPath: conditionPath,
            onChange: newCondition => onConditionChange(newCondition, false),
            property: property,
            readOnly: isReadOnly
          })
        }), condition && /*#__PURE__*/_jsx(ValueInput, {
          use: use,
          propertyType: property ? property.type : undefined,
          children: /*#__PURE__*/_jsx(EditorConditionValueInput, {
            condition: condition,
            disabled: disabled,
            filterFamily: baseFilterFamily,
            hideExtraInputs: !showExtraInputs,
            inputUse: inputUse,
            multiCollapseLimit: multiCollapseLimit,
            onChange: onConditionChange,
            readOnly: isReadOnly
          })
        }), condition && isIncludeObjectsWithNoValueSetCheckboxEnabled && /*#__PURE__*/_jsx(EditorIncludeObjectsWithNoValueSetInput, {
          condition: condition,
          conditionPath: conditionPath,
          filterFamily: baseFilterFamily,
          onChange: onConditionChange
        })]
      }), hasPropertiesLoaded && additionalComponents]
    })]
  });
};
export default PropertyFilterUI;